import React from 'react';
import { useBilling } from 'src/hooks/useBilling';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { useInsuranceStore } from 'src/store/insurance';
import { cn, isLollySkin } from '@utils/index';

export enum SubscriptionStatus {
  Unpaid = -3,
  PastDue = -2,
  Canceled = -1,
  Pending = 0,
  Active = 1,
  //Completed = 2,
}

const getPlanStatus = (status: number) => {
  if (status === SubscriptionStatus.Active) {
    return { text: 'Active', className: 'text-green-700' };
  }
  if (status === SubscriptionStatus.Pending) {
    return { text: 'Pending', className: 'text-yellow-700' };
  }
  if (status === SubscriptionStatus.Canceled) {
    return { text: 'Canceled', className: 'text-red-700' };
  }
  return { text: 'Inactive', className: 'text-gray-700' };
};

export const CurrentPlans = () => {
  const { subscriptionPlans, isLoading } = useBilling();
  const { plans: allPlans } = useInsuranceStore();

  const totalMonthlyAmount = subscriptionPlans?.reduce((acc, plan) => {
    const matchedPlan = allPlans?.find((p) => p.id === plan.planId);
    const matchedPrice = matchedPlan?.prices?.find(
      (pr) => pr.id === plan.priceId
    );
    return acc + (matchedPrice?.price ?? 0);
  }, 0);

  if (isLoading) {
    return (
      <section className="flex flex-col rounded-3xl bg-profile-section-bg pb-16 max-l:mt-0">
        <div className={cn('text-white px-15 max-md:px-10 rounded-t-3xl py-3.5 pb-2 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
          <p className="text-3xl 2xl:text-4xl">Current Plans</p>
        </div>
        <div className="flex justify-center items-center py-8">
          <div>
            <AnimatedDots />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col rounded-3xl bg-profile-section-bg pb-16 max-l:mt-0">
      <div className={cn('text-white px-15 max-md:px-10 rounded-t-3xl py-3.5 pb-2 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
        <p className="text-3xl 2xl:text-4xl">Current Plans</p>
      </div>
      <div className="flex flex-col px-13.5 mt-8 max-md:px-10 max-xs:px-5">
        <p className="text-xl tracking-wide max-w-[80%] max-l:max-w-full">
          This is a list of your current plans and prices along with your bank
          information. 
          {/* need to find out what it is the btn */}
          {/* If you have any questions concerning billing please feel
          free to contact a plan specialist here. */}
        </p>
        <hr className="mt-8.5" />
        <div className="flex justify-between mt-11 max-4xl:flex-col max-4xl:gap-10">
          <div className="flex flex-col flex-1 min-w-0 gap-4 max-4xl:w-1/2 w-full max-md:max-w-full">
            {subscriptionPlans && subscriptionPlans.length > 0 ? (
              subscriptionPlans.map((plan) => {
                const matchedPlan = allPlans?.find((p) => p.id === plan.planId);
                const planName =
                  matchedPlan?.name ||
                  matchedPlan?.marketingName ||
                  `Plan ${plan.planId}`;
                const matchedPrice = matchedPlan?.prices?.find(
                  (pr) => pr.id === plan.priceId
                );
                const priceNumber =
                  matchedPrice?.price ?? matchedPlan?.averagePrice;
                const priceText =
                  typeof priceNumber === 'number' ? `$${priceNumber}` : '—';

                return (
                  <div
                    key={plan.id}
                    className="grid grid-cols-1 gap-y-2 text-xl font-semibold text-profile-text-muted tracking-wide md:grid-cols-[minmax(0,1fr)_auto_auto] 4xl:grid-cols-[480px_max-content_max-content] md:gap-x-6 md:items-center max-sm:text-xl"
                  >
                    <span className="font-bold leading-tight text-profile-text-muted">
                      {planName}
                    </span>
                    <span className="text-black whitespace-nowrap md:text-right">
                      {priceText}
                    </span>
                    <span className="text-black whitespace-nowrap">
                      Status:
                      <span
                        className={`ml-2 font-semibold ${getPlanStatus(plan.status).className}`}
                      >
                        {getPlanStatus(plan.status).text}
                      </span>
                    </span>
                  </div>
                );
              })
            ) : (
              <span className="text-2xl font-bold text-profile-text-muted tracking-wider">
                No active plans
              </span>
            )}
          </div>
          <div className="flex flex-col w-[38%] mt-1.5 gap-6 max-xl:gap-4 max-md:w-full max-md:gap-2">
            <p className="uppercase font-bold text-2xl max-md:text-xl ml-2 max-4xl:ml-0 4xl:text-xl tracking-wider max-sm:text-lg">
              Total Premium:
            </p>
            <span className="ml-3 max-4xl:ml-0 text-7xl 4xl:text-[67px] max-md:text-5xl font-bold tracking-wide text-primary max-xl:text-6xl max-m:text-5xl max-sm:text-4xl">
              ${totalMonthlyAmount?.toFixed(2) ?? 0}/mo
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
